import { useMemo } from 'react';
import { UserRole } from '@/components/authnz/types/rbac';
import { useAuth } from '@/contexts/AuthContext';
import { decisionFromJson, makeDecision } from './decision';
import type { Decision } from './types';

function unauthenticatedDecision(): Decision {
  return makeDecision({
    uid: null,
    email: null,
    emailKey: null,
    existence: 'none',
    reason: { kind: 'invalid-credential' },
    roles: [],
  });
}

function decisionFromSessionRoles(params: {
  uid: string | null;
  email: string | null;
  roles: string[];
}): Decision {
  return makeDecision({
    uid: params.uid,
    email: params.email,
    emailKey: params.email ? params.email.trim().toLowerCase() : null,
    existence: params.uid ? 'existing' : 'none',
    reason: { kind: params.uid ? 'ok' : 'invalid-credential' },
    roles: params.roles.filter((role): role is UserRole =>
      Object.values(UserRole).includes(role as UserRole),
    ),
  });
}

/**
 * Browser Access Decision — same `can` / `explain` / `gate` as the server.
 * Empty roles are not revoke; route gates still ask `decision.can`.
 */
export function useAccess(): { decision: Decision; loading: boolean } {
  const { decisionJson, serverUser, loading } = useAuth();

  const decision = useMemo(() => {
    if (decisionJson) {
      return decisionFromJson(decisionJson);
    }
    if (serverUser) {
      return decisionFromSessionRoles({
        uid: serverUser.userId ?? null,
        email: serverUser.email ?? null,
        roles: serverUser.roles ?? [],
      });
    }
    return unauthenticatedDecision();
  }, [decisionJson, serverUser]);

  return { decision, loading };
}
